import React from 'react';
import { Link } from 'react-router-dom';
import { Buildings, Briefcase, Certificate, ShieldCheck, Star, Leaf } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';

export default function Home() {
  return (
    <div className="min-h-screen bg-background_light">
      <Navbar />
      
      {/* Hero Section */}
      <div
        className="relative h-[600px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1555424221-250de2a343ad)',
        }}
      >
        <div className="absolute inset-0 bg-primary/60"></div>
        <div className="relative h-full flex items-center justify-center px-6">
          <div className="max-w-4xl text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tight font-semibold text-text_light mb-6">
              PestPro Hub
            </h1>
            <p className="text-xl sm:text-2xl text-secondary leading-relaxed mb-8">
              Your Complete Pest Control Industry Platform — Find Companies, Jobs, Safety Resources, and Certification Training
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/companies"
                data-testid="hero-companies-btn"
                className="bg-accent text-white px-8 py-4 rounded-sm hover:bg-accent/90 transition-colors text-lg font-medium"
              >
                Browse Companies
              </Link>
              <Link
                to="/jobs"
                className="bg-white text-primary px-8 py-4 rounded-sm hover:bg-gray-100 transition-colors text-lg font-medium"
              >
                Find Jobs
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Company */}
      <div className="bg-accent/10 py-16 px-6 border-y-2 border-accent">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Star size={32} weight="fill" className="text-accent" />
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium">Featured Partner</h2>
          </div>
          <div className="bg-white border border-borders rounded-sm p-8">
            <h3 className="text-3xl tracking-tight font-medium mb-2">Blue Collar World Pest Control Services</h3>
            <p className="text-gray-600 mb-4">Pittsburgh, PA • BU#15680</p>
            <p className="text-base leading-relaxed mb-6">
              Professional pest control services serving the Pittsburgh area with expertise in general pest control, wildlife management, termite treatments, and wood-destroying insect control.
            </p>
            <a
              href="mailto:raymondjackson30@yahoo.com"
              className="inline-flex items-center gap-2 bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
              data-testid="featured-contact"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-4xl sm:text-5xl tracking-tight font-semibold text-center mb-12">
          Everything You Need
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link to="/companies" className="bg-white border border-borders rounded-sm p-8 hover:-translate-y-1 transition-transform" data-testid="feature-directory">
            <Buildings size={48} className="text-accent mb-4" weight="duotone" />
            <h3 className="text-2xl tracking-tight font-medium mb-3">Company Directory</h3>
            <p className="text-base leading-relaxed text-gray-700">
              Browse and search pest control companies by location and services. Find trusted professionals near you.
            </p>
          </Link>

          <Link to="/jobs" className="bg-white border border-borders rounded-sm p-8 hover:-translate-y-1 transition-transform" data-testid="feature-jobs">
            <Briefcase size={48} className="text-accent mb-4" weight="duotone" />
            <h3 className="text-2xl tracking-tight font-medium mb-3">Jobs Board</h3>
            <p className="text-base leading-relaxed text-gray-700">
              Find your next career opportunity. Browse openings for technicians, managers, and pest control professionals.
            </p>
          </Link>

          <Link to="/certifications" className="bg-white border border-borders rounded-sm p-8 hover:-translate-y-1 transition-transform" data-testid="feature-certifications">
            <Certificate size={48} className="text-accent mb-4" weight="duotone" />
            <h3 className="text-2xl tracking-tight font-medium mb-3">Certification Center</h3>
            <p className="text-base leading-relaxed text-gray-700">
              State-by-state study resources, exam prep materials, and official links for PA, OH, and VA licensing.
            </p>
          </Link>

          <Link to="/hypersensitivity" className="bg-white border border-borders rounded-sm p-8 hover:-translate-y-1 transition-transform" data-testid="feature-safety">
            <ShieldCheck size={48} className="text-accent mb-4" weight="duotone" />
            <h3 className="text-2xl tracking-tight font-medium mb-3">Safety Resource Center</h3>
            <p className="text-base leading-relaxed text-gray-700">
              Hypersensitivity registry information, pesticide ingredient database, and personal sensitivity profiles.
            </p>
          </Link>

          <Link to="/testimonials" className="bg-white border border-borders rounded-sm p-8 hover:-translate-y-1 transition-transform" data-testid="feature-testimonials">
            <Star size={48} className="text-accent mb-4" weight="duotone" />
            <h3 className="text-2xl tracking-tight font-medium mb-3">Customer Reviews</h3>
            <p className="text-base leading-relaxed text-gray-700">
              Read reviews and testimonials from real customers. Share your experience with pest control companies.
            </p>
          </Link>

          <Link to="/safety-training" className="bg-white border border-borders rounded-sm p-8 hover:-translate-y-1 transition-transform" data-testid="feature-safety-training">
            <ShieldCheck size={48} className="text-accent mb-4" weight="fill" />
            <h3 className="text-2xl tracking-tight font-medium mb-3">Safety Training Hub</h3>
            <p className="text-base leading-relaxed text-gray-700">
              OSHA-aligned ladder safety and PPE training with videos, quizzes, and printable study guides for technicians.
            </p>
          </Link>

          <Link to="/register" className="bg-white border border-borders rounded-sm p-8 hover:-translate-y-1 transition-transform" data-testid="feature-join">
            <Leaf size={48} className="text-accent mb-4" weight="duotone" />
            <h3 className="text-2xl tracking-tight font-medium mb-3">Join the Community</h3>
            <p className="text-base leading-relaxed text-gray-700">
              Register as a job seeker, employer, or company. Post jobs, submit listings, and connect with the industry.
            </p>
          </Link>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary text-text_light py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl tracking-tight font-semibold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl leading-relaxed text-secondary mb-8">
            Join PestPro Hub today and connect with the pest control industry.
          </p>
          <Link
            to="/register"
            data-testid="cta-register"
            className="inline-block bg-accent text-white px-8 py-4 rounded-sm hover:bg-accent/90 transition-colors text-lg font-medium"
          >
            Create Free Account
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-borders py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-gray-600">
          <p className="mb-2">© 2026 PestPro Hub. Owned and operated by Raymond T. Jackson, Blue Collar World Pest Control Services.</p>
          <p className="text-sm">Pittsburgh, PA • BU#15680 • <a href="mailto:raymondjackson30@yahoo.com" className="text-accent hover:underline">raymondjackson30@yahoo.com</a></p>
        </div>
      </footer>
    </div>
  );
}